// Learning Gaps — the data behind the left-hand panel of the Admin Learning
// Framework dashboard.
//
// Served by GET /v3/admin/learning-gaps (Dosen-v2-BE, docs/EPIC-learning-gaps.md):
// the org's Teams list and, per team, its gaps. A gap is normally a lesson
// whose assessments are failing; it can also be a theme Elliott inferred purely
// from learner questions, in which case it has no lesson or program.
//
// A gap is made of EPISODES. An episode is one surfacing of the gap: the
// failures and questions that raised it (its Report) and what Elliott proposes
// doing about it (its Recommendation Set). Clearing every recommendation in an
// episode closes the gap. If evidence then recurs, a new episode opens and the
// gap goes live again. Earlier episodes stay on screen, greyed, as the record
// of what was already tried. The BE only opens a new episode once the previous
// one is fully actioned, which is what makes it evidence: if Update Lesson was
// ticked in episode 1 and episode 2 exists, the content fix did not hold.
//
// Done / ignored ticks persist through PATCH …/recommendations/{key}; the BE
// decides closure from the same required-keys rule as `recommendationKeysFor`.

/** A learner who failed a live scenario, with the verdict Elliott already
 *  writes on the Org Members report. */
export interface ScenarioFailure {
  id: string;
  name: string;
  assessment: string;
}

/** A learner who got a quiz question wrong, with what they picked. */
export interface QuizFailure {
  id: string;
  name: string;
  assessment: string;
  question: string;
  answer: string;
  correctAnswer: string;
}

/** A question a learner asked Elliott in chat. */
export interface ElliottQuery {
  id: string;
  userName: string;
  question: string;
  // Whether grounding content came back from the Knowledge hub.
  status: 'Answer Retrieved' | 'Answer Not Found';
  // What Elliott replied. Empty when the BE no longer has it.
  response: string;
}

/** One attribute list's contribution to the impacted audience — mirrors the
 *  AudienceRule stored on the program. */
export interface ImpactedGroup {
  listName: string;
  values: string[];
  /** Attribute value ids, same order as values. Empty on older gaps. */
  valueIds: string[];
}

export interface RecommendedCourse {
  name: string;
  audiences: string[];
  /** The same audience as attribute value ids — exact where labels can clash. */
  audienceValueIds: string[];
  /**
   * The audience is roles (a capability recommendation), not attribute values:
   * `audiences` names the roles, and nothing is matched to the org's attribute
   * values by those names.
   */
  audienceIsRoles?: boolean;
  purpose: string;
  tone: string[];
  documents: string[];
}

export interface KnowledgeRecommendation {
  /** What to add to the Knowledge hub. Several entries read as one sentence. */
  topics: string[];
}

export type RecommendationKey = 'course' | 'knowledge' | 'assessment' | 'improve' | 'discuss';

export type RecommendationStatus = 'done' | 'ignored';

/**
 * One surfacing of a gap: the evidence that raised it, and what Elliott
 * proposes doing about it.
 */
export interface GapEpisode {
  id: string;
  /** 1-based, in opening order. */
  n: number;
  /** Unix seconds the episode opened. */
  openedAt: number;
  /** Unix seconds the BE closed it (every required recommendation actioned), else null. */
  closedAt: number | null;
  scenarioFailures: ScenarioFailure[];
  quizFailures: QuizFailure[];
  elliottQueries: ElliottQuery[];
  /** A course is a big undertaking, so Elliott only recommends one when the
   *  evidence is large enough to justify it. Null otherwise. */
  course: RecommendedCourse | null;
  knowledge: KnowledgeRecommendation;
  /** Whether Elliott would also put a skills assessment behind this episode. */
  suggestsAssessment: boolean;
  /** The admin's ticks, as persisted. */
  status: Partial<Record<RecommendationKey, RecommendationStatus>>;
  /** Who set each tick — every admin and manager who sees the gap sees the same ticks. */
  statusBy: Partial<Record<RecommendationKey, { name: string; at: number }>>;
}

export interface Gap {
  id: string;
  key: string;
  kind: 'lesson' | 'query';
  // Lesson title, or a title Elliott coined for a query-only gap.
  title: string;
  // The program the lesson belongs to. Null for query-only gaps.
  programId: string | null;
  programName: string | null;
  moduleTitle: string;
  impactedGroups: ImpactedGroup[];
  /** Oldest first. The last entry is the live episode. */
  episodes: GapEpisode[];
  /** The viewer can edit this gap's lesson (org admin, or admin of its program). */
  canEditLesson: boolean;
  /** Otherwise who to ask: the program's owner, else its creator. */
  lessonContact: LessonContact | null;
}

export interface LessonContact {
  userId: string;
  name: string;
  /** Chat identity (ACS communication id) — empty when they have none. */
  communicationId: string;
}

export interface TeamGaps {
  id: string;
  name: string;
  gaps: Gap[];
}

/** GET /v3/admin/learning-gaps */
export interface LearningGapsResponse {
  teamsList: { listId: string; listName: string } | null;
  teams: TeamGaps[];
  lastComputedAt: number | null;
}

/** The BE's episode carries the course without an audience; the FE fills it
 *  from the gap's impacted groups so the review chat and cards can show it. */
export const mapApiGap = (raw: any): Gap => {
  const impactedGroups: ImpactedGroup[] = (Array.isArray(raw?.impactedGroups) ? raw.impactedGroups : []).map(
    (g: any): ImpactedGroup => ({
      listName: String(g?.listName ?? ''),
      values: Array.isArray(g?.values) ? g.values.map(String) : [],
      valueIds: Array.isArray(g?.valueIds) ? g.valueIds.map(String) : []
    })
  );
  const audiences = impactedGroups.flatMap((g) => g.values);
  const audienceValueIds = impactedGroups.flatMap((g) => g.valueIds);
  const contact = raw?.lessonContact;
  return {
    // A BE that predates team managers only served people who could edit.
    canEditLesson: raw?.canEditLesson !== false,
    lessonContact: contact?.userId
      ? {
          userId: String(contact.userId),
          name: String(contact.name || ''),
          communicationId: String(contact.communicationId || '')
        }
      : null,
    id: String(raw?.id ?? ''),
    key: String(raw?.key ?? ''),
    kind: raw?.kind === 'query' ? 'query' : 'lesson',
    title: String(raw?.title ?? ''),
    programId: raw?.programId ? String(raw.programId) : null,
    programName: raw?.programName ? String(raw.programName) : null,
    moduleTitle: String(raw?.moduleTitle ?? ''),
    impactedGroups,
    episodes: (Array.isArray(raw?.episodes) ? raw.episodes : []).map((ep: any) => ({
      id: String(ep?.id ?? ''),
      n: Number(ep?.n) || 1,
      openedAt: Number(ep?.openedAt) || 0,
      closedAt: ep?.closedAt ? Number(ep.closedAt) : null,
      scenarioFailures: Array.isArray(ep?.scenarioFailures) ? ep.scenarioFailures : [],
      quizFailures: Array.isArray(ep?.quizFailures) ? ep.quizFailures : [],
      elliottQueries: (Array.isArray(ep?.elliottQueries) ? ep.elliottQueries : []).map(
        (q: any): ElliottQuery => ({
          id: String(q?.id ?? ''),
          userName: String(q?.userName ?? ''),
          question: String(q?.question ?? ''),
          status: q?.status === 'Answer Not Found' ? 'Answer Not Found' : 'Answer Retrieved',
          response: typeof q?.response === 'string' ? q.response : ''
        })
      ),
      course: ep?.course?.name
        ? {
            name: String(ep.course.name),
            audiences,
            audienceValueIds,
            purpose: String(ep.course.purpose ?? ''),
            tone: Array.isArray(ep.course.tone) ? ep.course.tone : [],
            documents: Array.isArray(ep.course.documents) ? ep.course.documents : []
          }
        : null,
      knowledge: { topics: Array.isArray(ep?.knowledge?.topics) ? ep.knowledge.topics : [] },
      suggestsAssessment: ep?.suggestsAssessment === true,
      status: ep?.status && typeof ep.status === 'object' ? ep.status : {},
      statusBy: ep?.statusBy && typeof ep.statusBy === 'object' ? ep.statusBy : {}
    }))
  };
};

/**
 * The org's attribute values a recommended course opens with as its audience:
 * by id when the course carries them; by label for gaps from before ids were
 * kept. An audience of roles (a capability recommendation) opens with none: a
 * role is not an attribute value, and a value that happens to share the role's
 * name (a Job Title of the same name, say) must not become the audience.
 */
export const seededAudienceIds = (
  course: Pick<RecommendedCourse, 'audiences' | 'audienceValueIds' | 'audienceIsRoles'>,
  options: Array<{ id: string; label: string }>
): string[] => {
  if (course.audienceIsRoles) return [];
  const byId = new Set(course.audienceValueIds);
  const wanted = course.audiences.map((a) => a.trim().toLowerCase());
  return options
    .filter((o) => (byId.size > 0 ? byId.has(o.id) : wanted.includes(o.label.trim().toLowerCase())))
    .map((o) => o.id);
};

/** The episode currently being worked — the last one opened. */
export const currentEpisode = (gap: Gap): GapEpisode | undefined => gap.episodes[gap.episodes.length - 1];

/** Total evidence behind a gap, across every episode — drives the chip. */
export const gapOccurrences = (gap: Gap): number =>
  gap.episodes.reduce(
    (n, e) => n + e.scenarioFailures.length + e.quizFailures.length + e.elliottQueries.length,
    0
  );

/** Learners failed the lesson in this episode — a live scenario or a quiz answer. */
const hasLessonFailures = (episode: GapEpisode): boolean =>
  episode.scenarioFailures.length + episode.quizFailures.length > 0;

/**
 * An episode built only from questions to Elliott. It always gets Add
 * Knowledge and Discuss with Learner, plus Publish Course once 3+ questions
 * sit behind it, and nothing else — even when the BE matched its questions to
 * a lesson.
 */
export const isQuestionsOnly = (episode: GapEpisode): boolean => !hasLessonFailures(episode);

/**
 * Sub copy under the gap title — where the gap came from. A gap whose evidence
 * is only questions to Elliott reads as such even when the BE matched it to a
 * lesson; naming the course there implies learners failed it.
 */
export const gapSourceLabel = (gap: Gap): string =>
  gap.episodes.some(hasLessonFailures) && gap.programName ? `${gap.programName} Course` : 'Questions to Elliott';

/**
 * Update Lesson is offered only where learners failed the lesson. The BE
 * attaches questions to a lesson when they're about it, but questions alone
 * say nothing against the lesson's content — so an episode built only from
 * questions gets the question-gap recommendations, lesson or not.
 */
export const offersUpdateLesson = (episode: GapEpisode, kind: Gap['kind']): boolean =>
  kind === 'lesson' && hasLessonFailures(episode);

/**
 * Which recommendations count toward closing an episode. Run Skills Assessment
 * is deliberately excluded — it isn't live yet, so it can't be actioned and
 * must not block a gap from closing. Same rule as the BE's `requiredKeys`
 * (learningGapsService).
 */
export const recommendationKeysFor = (episode: GapEpisode, kind: Gap['kind']): RecommendationKey[] => {
  const keys: RecommendationKey[] = [];
  const questionsOnly = isQuestionsOnly(episode);
  if (offersUpdateLesson(episode, kind)) keys.push('improve');
  // Question rounds always; otherwise only when named learners failed.
  if (questionsOnly || discussLearnerNames(episode).length > 0) keys.push('discuss');
  // Only required when Elliott actually recommended one — for a question
  // round, the BE does once 3+ questions sit behind it.
  if (episode.course) keys.push('course');
  if (questionsOnly || episode.elliottQueries.some((q) => q.status === 'Answer Not Found')) keys.push('knowledge');
  return keys;
};

/** Every actionable recommendation in this episode is done or ignored. */
export const isEpisodeActioned = (
  episode: GapEpisode,
  statusFor: (episodeId: string, key: RecommendationKey) => RecommendationStatus | undefined,
  kind: Gap['kind']
): boolean =>
  episode.closedAt != null ||
  recommendationKeysFor(episode, kind).every((key) => Boolean(statusFor(episode.id, key)));

/**
 * Episodes the admin can see. A new report and recommendation set is only
 * surfaced once the preceding one has been fully actioned, so the list runs to
 * the first unactioned episode and stops there.
 */
export const visibleEpisodes = (
  gap: Gap,
  statusFor: (episodeId: string, key: RecommendationKey) => RecommendationStatus | undefined
): GapEpisode[] => {
  const out: GapEpisode[] = [];
  for (const episode of gap.episodes) {
    out.push(episode);
    if (!isEpisodeActioned(episode, statusFor, gap.kind)) break;
  }
  return out;
};

/** A gap is closed once every episode on it is actioned. */
export const isGapClosed = (
  gap: Gap,
  statusFor: (episodeId: string, key: RecommendationKey) => RecommendationStatus | undefined
): boolean => gap.episodes.length > 0 && gap.episodes.every((e) => isEpisodeActioned(e, statusFor, gap.kind));

const joinTopics = (topics: string[]): string =>
  topics.length > 1
    ? `${topics.slice(0, -1).join(', ')} and ${topics[topics.length - 1]}`
    : topics[0] ?? 'this topic';

/**
 * Add Knowledge copy. A question round shows it even when Elliott answered
 * everything; the reason changes, and with no topics from Elliott (rounds
 * opened before it was asked for them) the gap's own title stands in.
 */
export const knowledgeRecommendationCopy = (episode: GapEpisode, gapTitle: string): string => {
  const topics = episode.knowledge.topics.length ? episode.knowledge.topics : [gapTitle];
  const onePagers = topics.length > 1 ? 'one-pagers' : 'a one-pager';
  return episode.elliottQueries.some((q) => q.status === 'Answer Not Found')
    ? `I could not answer learner questions on this. I recommend adding ${onePagers} to Knowledge covering ${joinTopics(
        topics
      )}, so I can answer these questions next time.`
    : `I answered these questions from my Knowledge hub, but learners keep asking about this. I'd add ${onePagers} covering ${joinTopics(
        topics
      )} so it's all in one place.`;
};

/** Copy for the recommendation that fixes the lesson rather than adding to it. */
export const improveLessonCopy = (): string =>
  'If the lesson itself may be causing this gap, open the editor to review and update the lesson. Everyone who has not yet taken the lesson will get the improved version.';

/** The same recommendation for someone who can't edit the lesson: ask whoever can. */
export const askToUpdateLessonCopy = (contactName: string | null): string =>
  `If you think this gap is occurring because of the lesson content itself, ask ${
    contactName || "the program's owner"
  } to update it as per your recommendation, or ask them to make you an admin so that you can go to the editor to review and update it as you see fit yourself. That stops the gap forming again, as everyone who has yet to take the lesson will get the improved version.`;

// What the BE names an asker it couldn't find — not someone to talk to.
const UNKNOWN_ASKER = 'A learner';

/**
 * The people to follow up with, once each, in report order: whoever failed a
 * live scenario or quiz answer — or, in a question round, whoever asked.
 */
export const discussLearnerNames = (episode: GapEpisode): string[] => {
  const names = isQuestionsOnly(episode)
    ? episode.elliottQueries.map((q) => q.userName.trim()).filter((n) => n && n !== UNKNOWN_ASKER)
    : [...episode.scenarioFailures, ...episode.quizFailures].map((f) => f.name.trim()).filter(Boolean);
  return Array.from(new Set(names));
};

export const discussWithLearnerCopy = (names: string[]): string =>
  names.length === 0
    ? 'Address these questions directly with the learners who asked them.'
    : `Address ${names.length > 1 ? 'these occurrences' : 'this occurrence'} directly with ${joinTopics(names)}.`;

/** Plain text of Elliott's markdown reply, for the narrow gap panel. */
export const responsePlainText = (markdown: string): string =>
  markdown
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/`(.+?)`/g, '$1')
    .replace(/^(\s*)[-*]\s+/gm, '$1• ')
    .replace(/^#{1,6}\s+/gm, '')
    .trim();
